/** Print template document schema + element factories */
import { normalizeColorSpans } from '../utils/textRuns';
import { QR_LOGO_DEFAULTS } from '../utils/qrCode';
import { DEFAULT_SHAPE, shapeElementDefaults } from './shapeLibrary';

export const TEMPLATE_VERSION = 1;

export const ELEMENT_TYPES = {
    TEXT: 'text',
    IMAGE: 'image',
    SHAPE: 'shape',
    QRCODE: 'qrcode',
    BARCODE: 'barcode',
    TABLE: 'table',
    PLACEHOLDER: 'placeholder',
};

/**
 * Types whose proportions matter, so they offer Figma's "constrain proportions".
 * Images, QR codes and SHAPES start locked — a stretched photo looks wrong, a
 * stretched QR can stop scanning, and a shape is drawn as a shape: a star, a
 * hexagon or a triangle skewed by a careless corner drag stops reading as one.
 * Whoever wants an oval or a squashed chevron unlocks it, which is one click and
 * is remembered on that element. Barcodes are the exception — they are routinely
 * made wider or shorter to fit a label — so they start free.
 */
const ASPECT_LOCK_TYPES = [
    ELEMENT_TYPES.IMAGE,
    ELEMENT_TYPES.QRCODE,
    ELEMENT_TYPES.BARCODE,
    ELEMENT_TYPES.SHAPE,
];
const ASPECT_LOCK_DEFAULT_ON = [
    ELEMENT_TYPES.IMAGE,
    ELEMENT_TYPES.QRCODE,
    ELEMENT_TYPES.SHAPE,
];

/** Takes the whole element (not just the type) so per-shape rules can live here. */
export function supportsAspectLock(el) {
    return !!el && ASPECT_LOCK_TYPES.includes(el.type);
}

/**
 * What a layer can share with a linked group (`contentKey`), or null when it has
 * nothing to share: its words, its picture, or its grid.
 *
 * A table is its own kind: its content is a grid of cells, and pouring that into
 * a text box — or a line of text into a table — would mean nothing. Text is
 * checked before the rest, so a QR code and a barcode — which carry a value the
 * same way a heading carries words — link as text and can follow the very text
 * field they encode. A QR's logo travels too, but only between QR codes (see
 * linkedPayload in the store): a heading has nowhere to put a picture.
 *
 * A photo cannot follow a text field, which is why the KIND matters and not
 * merely the ability to link.
 */
export function linkKind(el) {
    if (el?.type === ELEMENT_TYPES.TABLE) return 'table';
    if (typeof el?.content === 'string') return 'text';
    if (el?.type === ELEMENT_TYPES.IMAGE) return 'image';
    return null;
}

/**
 * The panel section that guards a layer's fill-in content — its words, its
 * picture, its code or its grid.
 *
 * One definition for the store (which refuses the write) and the fill-in list
 * (which must not offer a box the store will refuse), or a locked QR code shows
 * the user a field that silently does nothing.
 */
export function contentLockKey(el) {
    switch (el?.type) {
        case ELEMENT_TYPES.IMAGE: return 'image';
        case ELEMENT_TYPES.QRCODE: return 'qr';
        case ELEMENT_TYPES.BARCODE: return 'barcode';
        case ELEMENT_TYPES.TABLE: return 'table';
        default: return 'content';
    }
}

/**
 * A line is the one shape that must NOT start locked: its box height is the
 * thickness of the rule, so locking it would fatten the line as it is drawn out.
 */
const ASPECT_FREE_SHAPES = ['line'];

/** Explicit `lockAspect` wins; otherwise fall back to the per-type default. */
export function isAspectLocked(el) {
    if (!supportsAspectLock(el)) return false;
    if (el.lockAspect != null) return !!el.lockAspect;
    if (el.type === ELEMENT_TYPES.SHAPE && ASPECT_FREE_SHAPES.includes(el.shape)) return false;
    return ASPECT_LOCK_DEFAULT_ON.includes(el.type);
}

export const CANVAS_MIN = 100;
export const CANVAS_MAX = 4000;

/**
 * Figma-style effect defaults, shared by every element factory so the panel,
 * the canvas renderer and the PNG/PDF export all read the same keys. These are
 * the NEW effects added on top of the original drop/inner shadow fields
 * (`shadow*` / `innerShadow*` already live on each factory). Absent on an older
 * template → undefined, which every resolver reads as "off", so no migration.
 *
 * Effect families:
 *  - Shadow spread   — `shadowSpread` / `innerShadowSpread` (CSS box-shadow spread)
 *  - Layer blur      — `layerBlur` (CSS filter: blur — blurs the element itself)
 *  - Background blur — `backdropBlur` (CSS backdrop-filter: blur — blurs behind)
 *  - Noise           — grain overlay clipped to the element
 *  - Texture         — procedural pattern overlay (paper / grain / lines / …)
 *  - Shader          — procedural gradient field (mesh / aurora / holographic / …)
 *  - Glass           — frosted backdrop + tint + edge highlight
 */
export const EFFECT_DEFAULTS = {
    shadowSpread: 0,
    innerShadowSpread: 0,
    layerBlur: 0,
    backdropBlur: 0,
    // Noise — amount doubles as the on/off gate (0 = off)
    noiseAmount: 0,
    noiseSize: 2,
    noiseDensity: 100,
    noiseType: 'mono', // 'mono' | 'color'
    noiseBlend: 'overlay',
    // Texture — type 'none' = off
    textureType: 'none', // 'paper'|'grain'|'lines'|'cross'|'dots'|'grid'
    textureScale: 6,
    textureOpacity: 40,
    textureColor: '#000000',
    textureBlend: 'multiply',
    // Shader — type 'none' = off
    shaderType: 'none', // 'mesh'|'aurora'|'holographic'|'sunset'
    shaderColor1: '#ff5d8f',
    shaderColor2: '#4d7cff',
    shaderColor3: '#9d4dff',
    shaderAngle: 45,
    shaderOpacity: 100,
    shaderBlend: 'normal',
    // Glass — blur doubles as the on/off gate (0 = off)
    glassBlur: 0,
    glassTint: '#ffffff',
    glassOpacity: 12,
    glassHighlight: true,
};

/** Every effect style key — the panel adds these to placeholder allowedStyles. */
export const EFFECT_STYLE_KEYS = Object.keys(EFFECT_DEFAULTS);

/**
 * Proportional page resize — the canvas counterpart of the element aspect lock.
 * Scales `origin` so the driving axis lands as close to `want` as the min/max
 * limits on BOTH axes allow: folding height's limits into the width clamp is
 * what keeps the ratio exact at the extremes, instead of one axis hitting its
 * bound and quietly squashing the page.
 */
export function constrainCanvasSize(origin, want) {
    const ratio = origin.width / origin.height;
    if (!Number.isFinite(ratio) || ratio <= 0) return { width: origin.width, height: origin.height };
    const target = want.width != null ? want.width : want.height * ratio;
    const width = Math.min(
        Math.min(CANVAS_MAX, CANVAS_MAX * ratio),
        Math.max(Math.max(CANVAS_MIN, CANVAS_MIN * ratio), target)
    );
    return { width: Math.round(width), height: Math.round(width / ratio) };
}

export const SIZE_PRESETS = [
    { id: 'business-card', label: 'Business Card', width: 1050, height: 600 },
    { id: 'business-card-portrait', label: 'Business Card (Portrait)', width: 600, height: 1050 },
    { id: 'a4', label: 'A4', width: 794, height: 1123 },
    { id: 'a4-landscape', label: 'A4 Landscape', width: 1123, height: 794 },
    { id: 'square', label: 'Square', width: 800, height: 800 },
    { id: 'banner', label: 'Banner', width: 1200, height: 400 },
    { id: 'custom', label: 'Custom', width: 800, height: 600 },
];

/**
 * The families the editor loads EAGERLY (they are in index.html's stylesheet),
 * so the chrome and a fresh document have type before anything is fetched.
 *
 * This is no longer the list the font picker offers — that lives in
 * `../fonts` and covers the whole Google Fonts library, loaded on demand.
 */
export const PRELOADED_FONT_FAMILIES = [
    // Bengali (loaded via Google Fonts in index.html)
    'Hind Siliguri',
    'Noto Sans Bengali',
    'Noto Serif Bengali',
    'Baloo Da 2',
    'Anek Bangla',
    'Tiro Bangla',
    'Galada',
    'Mina',
    'Atma',
    'Alkatra',
    // Latin / system
    'Inter',
    'Georgia',
    'Times New Roman',
    'Courier New',
    'Arial',
    'Helvetica',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Palatino Linotype',
];

export function createId(prefix = 'el') {
    return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

/* ------------------------------------------------------------------ *
 *  Frames (artboards)
 *
 *  A document holds ANY NUMBER of frames — a card front and back, a set of
 *  sizes, several designs side by side — each with its own size preset,
 *  background and clip setting. Frames live on one shared scene, so:
 *
 *   - `frame.x/y` place the frame ON the scene,
 *   - `element.x/y` stay SCENE coordinates (unchanged from the single-artboard
 *     model, so no saved template needs its geometry rewritten),
 *   - `element.frameId` says which frame the element belongs to, i.e. which
 *     export it lands in and which frame clips it.
 *
 *  Elements deliberately stay a FLAT top-level array rather than nesting inside
 *  frames: the backend's media lifting walks `canvasJson.elements`, and nesting
 *  would have silently stopped it finding images.
 *
 *  `document.canvas` survives as a read-only MIRROR of the first frame. Nothing
 *  in the editor reads it any more; it is kept so a saved template still opens in
 *  an older build (and so the value the API has always seen doesn't disappear).
 * ------------------------------------------------------------------ */

/** Gap left between frames when a new one is placed automatically. */
export const FRAME_GAP = 80;

export function createFrame({
    name,
    x = 0,
    y = 0,
    width = 1050,
    height = 600,
    background = '#ffffff',
    presetId,
    // New frames clip by default: an artboard is a page, and artwork bleeding past
    // its edge (an imported design, a dragged photo) reads as a bug on canvas while
    // the export — which always crops to the frame — shows it clipped anyway.
    // Existing templates are untouched: normalizeFrame keeps a stored `false`, and a
    // legacy frame with no flag at all stays unclipped (see below).
    clipContent = true,
} = {}) {
    const match = SIZE_PRESETS.find(
        (p) => p.id !== 'custom' && p.width === width && p.height === height
    );
    return {
        id: createId('frame'),
        name: name || 'Frame 1',
        x: Math.round(x),
        y: Math.round(y),
        width: Math.round(width),
        height: Math.round(height),
        background,
        backgroundType: 'solid',
        presetId: presetId || match?.id || 'business-card',
        lockAspect: false,
        clipContent: !!clipContent,
    };
}

function normalizeFrame(raw = {}, index = 0) {
    const width = Math.max(1, Number(raw.width) || 1050);
    const height = Math.max(1, Number(raw.height) || 600);
    const match = SIZE_PRESETS.find(
        (p) => p.id !== 'custom' && p.width === width && p.height === height
    );
    return {
        id: raw.id ? String(raw.id) : createId('frame'),
        name: raw.name || `Frame ${index + 1}`,
        x: Math.round(Number(raw.x) || 0),
        y: Math.round(Number(raw.y) || 0),
        width,
        height,
        background: raw.background || '#ffffff',
        // Page fill can be a gradient too; keep these so save/load round-trips it.
        backgroundType: raw.backgroundType || 'solid',
        gradientColor1: raw.gradientColor1,
        gradientColor2: raw.gradientColor2,
        gradientAngle: raw.gradientAngle,
        gradientCenterX: raw.gradientCenterX,
        gradientCenterY: raw.gradientCenterY,
        presetId: raw.presetId || match?.id || 'custom',
        // Frame proportions — same "constrain proportions" idea as the element lock,
        // but off by default: a frame starts as whatever size it was authored at.
        lockAspect: !!raw.lockAspect,
        // Hide anything hanging past the frame edge. New frames turn this ON
        // (createFrame), but a stored document keeps whatever it saved — a template
        // authored before the flag existed still renders exactly as it was drawn.
        clipContent: !!raw.clipContent,
        // Which global colour each of the frame's paints belongs to, and what it
        // was painted with before that global was ever edited (see utils/colorUsage).
        // Whitelisted fields, so they have to be named here or a saved template
        // comes back with its colour presets merged by value and unresettable.
        ...(raw.colorTokens && typeof raw.colorTokens === 'object'
            ? { colorTokens: { ...raw.colorTokens } }
            : null),
        ...(raw.presetBase && typeof raw.presetBase === 'object'
            ? { presetBase: { ...raw.presetBase } }
            : null),
    };
}

/** Legacy mirror of the first frame — see the note above. */
function canvasFromFrame(frame) {
    return {
        width: frame.width,
        height: frame.height,
        background: frame.background,
        backgroundType: frame.backgroundType,
        gradientColor1: frame.gradientColor1,
        gradientColor2: frame.gradientColor2,
        gradientAngle: frame.gradientAngle,
        gradientCenterX: frame.gradientCenterX,
        gradientCenterY: frame.gradientCenterY,
        presetId: frame.presetId,
        lockAspect: frame.lockAspect,
        clipContent: frame.clipContent,
    };
}

/**
 * Re-derive the legacy `canvas` mirror. Call after any change to `frames`.
 *
 * An EMPTY frame list is allowed — deleting every frame is a legitimate way to
 * start over, so the mirror falls back to a default page rather than a frame being
 * conjured back into the document.
 */
export function withFrames(doc, frames) {
    const list = Array.isArray(frames) ? frames : [];
    return {
        ...doc,
        frames: list,
        canvas: list.length ? canvasFromFrame(list[0]) : canvasFromFrame(createFrame()),
    };
}

export function getFrames(doc) {
    return Array.isArray(doc?.frames) && doc.frames.length ? doc.frames : [];
}

export function frameById(doc, id) {
    return getFrames(doc).find((f) => f.id === id) || null;
}

export function firstFrame(doc) {
    return getFrames(doc)[0] || null;
}

/** Elements belonging to one frame, in z-order. */
export function frameElements(doc, frameId) {
    return getSortedElements(doc?.elements || []).filter((el) => el.frameId === frameId);
}

/** Bounds of every frame together — what "zoom to fit" has to cover. */
export function sceneBounds(doc) {
    const frames = getFrames(doc);
    if (!frames.length) return { minX: 0, minY: 0, maxX: 1050, maxY: 600 };
    return frames.reduce((acc, f) => ({
        minX: Math.min(acc.minX, f.x),
        minY: Math.min(acc.minY, f.y),
        maxX: Math.max(acc.maxX, f.x + f.width),
        maxY: Math.max(acc.maxY, f.y + f.height),
    }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
}

/** Topmost frame under a scene point (later frames win, as they draw on top). */
export function frameAtPoint(frames = [], x, y) {
    for (let i = frames.length - 1; i >= 0; i -= 1) {
        const f = frames[i];
        if (x >= f.x && x <= f.x + f.width && y >= f.y && y <= f.y + f.height) return f;
    }
    return null;
}

/**
 * Which frame a box belongs to: the one holding its CENTRE. That is what decides
 * an element's frame after a drag, so dragging a design from one frame to another
 * moves it between exports the moment it is more in the new frame than the old.
 * Falls back to the frame it came from, then to the first frame, so an element
 * parked on empty scene never becomes an orphan that no export would include.
 */
export function frameForBox(frames = [], box = {}, fallbackId = null) {
    const cx = (Number(box.x) || 0) + (Number(box.width) || 0) / 2;
    const cy = (Number(box.y) || 0) + (Number(box.height) || 0) / 2;
    const hit = frameAtPoint(frames, cx, cy);
    if (hit) return hit.id;
    if (fallbackId && frames.some((f) => f.id === fallbackId)) return fallbackId;
    // No frames at all (every one deleted) → no home, and `null` is honest: the
    // element belongs to no export until a frame exists again.
    return frames[0]?.id || null;
}

/** Where a new frame goes: to the right of everything, on the top row. */
export function nextFrameOrigin(frames = [], width = 1050) {
    if (!frames.length) return { x: 0, y: 0 };
    const maxX = Math.max(...frames.map((f) => f.x + f.width));
    const minY = Math.min(...frames.map((f) => f.y));
    return { x: Math.round(maxX + FRAME_GAP), y: Math.round(minY), width };
}

/** "Frame 3" — the lowest number not already taken. */
export function nextFrameName(frames = []) {
    const used = new Set(frames.map((f) => f.name));
    let n = frames.length + 1;
    while (used.has(`Frame ${n}`)) n += 1;
    return `Frame ${n}`;
}

export function createEmptyDocument({ width = 1050, height = 600, background = '#ffffff', presetId } = {}) {
    const frame = createFrame({ name: 'Frame 1', width, height, background, presetId });
    return {
        version: TEMPLATE_VERSION,
        frames: [frame],
        canvas: canvasFromFrame(frame),
        elements: [],
        groups: [],
    };
}

/**
 * Layer groups are pure editor metadata: elements keep absolute x/y and an
 * optional `groupId`; `document.groups` is a `{ id, name, parentId }` registry.
 * The export renderer ignores both, so grouped documents render identically.
 *
 * NESTING: `groups[].parentId` makes groups a tree (Illustrator sub-layers).
 * `element.groupId` always names the INNERMOST group the element sits in;
 * its full ancestry is walked through `parentId`. `parentId: null` = top level.
 */

/** Sentinel key for "top level" in the parent-keyed maps below. */
const ROOT_KEY = ' root';

/** Ancestry of a group, outermost → the group itself. */
export function getGroupAncestry(groups = [], groupId) {
    if (!groupId) return [];
    const byId = new Map(groups.map((g) => [g.id, g]));
    const chain = [];
    let cur = byId.get(groupId);
    while (cur && chain.length <= groups.length) {
        chain.unshift(cur);
        cur = cur.parentId ? byId.get(cur.parentId) : null;
    }
    return chain;
}

/** Every group id inside `groupId` (itself included). */
export function getGroupSubtreeIds(groups = [], groupId) {
    const out = new Set([groupId]);
    let grew = true;
    while (grew) {
        grew = false;
        groups.forEach((g) => {
            if (g.parentId && out.has(g.parentId) && !out.has(g.id)) { out.add(g.id); grew = true; }
        });
    }
    return out;
}

/** True when `groupId` is `container` or nested anywhere inside it. */
export function isGroupInside(groups = [], groupId, container) {
    if (!groupId || !container) return false;
    return getGroupSubtreeIds(groups, container).has(groupId);
}

/** Elements inside a group, including every nested sub-group, in z-order. */
export function getGroupElements(elements = [], groups = [], groupId) {
    if (!groupId) return [];
    const ids = getGroupSubtreeIds(groups, groupId);
    return getSortedElements(elements).filter((e) => e.groupId && ids.has(e.groupId));
}

/**
 * Drop groups with fewer than 2 direct children (elements + sub-groups),
 * reparenting whatever they held up to their own parent, and clear dangling
 * element groupIds. Runs to a fixpoint so wrapper chains collapse in one pass —
 * an Illustrator import full of single-child wrappers flattens to real layers.
 */
export function pruneGroups(groups = [], elements = []) {
    let gs = (groups || [])
        .filter((g) => g && g.id)
        .map((g) => ({ id: String(g.id), name: g.name || 'Group', parentId: g.parentId ? String(g.parentId) : null }));
    let els = elements;

    // Detach dangling / cyclic parents so the walks below always terminate.
    const byId = new Map(gs.map((g) => [g.id, g]));
    gs.forEach((g) => {
        let n = g.parentId;
        let steps = 0;
        while (n && byId.has(n) && steps <= gs.length) { n = byId.get(n).parentId; steps += 1; }
        if ((n && !byId.has(n)) || steps > gs.length) g.parentId = null;
    });

    let changed = true;
    while (changed) {
        changed = false;
        const elCount = {};
        els.forEach((e) => { if (e.groupId) elCount[e.groupId] = (elCount[e.groupId] || 0) + 1; });
        const subCount = {};
        gs.forEach((g) => { if (g.parentId) subCount[g.parentId] = (subCount[g.parentId] || 0) + 1; });

        const dead = new Map(
            gs.filter((g) => ((elCount[g.id] || 0) + (subCount[g.id] || 0)) < 2).map((g) => [g.id, g])
        );
        if (dead.size) {
            changed = true;
            // Walk UP past every dead ancestor to the first survivor. Resolving only
            // one level would hand children a parent that is being removed in this
            // same pass, and they'd be orphaned to the top level — which silently
            // breaks z-order contiguity for the whole layer.
            const resolveLive = (gid) => {
                let cur = gid;
                let guard = 0;
                while (cur && dead.has(cur) && guard <= dead.size) {
                    cur = dead.get(cur).parentId || null;
                    guard += 1;
                }
                return cur || null;
            };
            els = els.map((e) => (dead.has(e.groupId) ? { ...e, groupId: resolveLive(e.groupId) } : e));
            gs = gs
                .filter((g) => !dead.has(g.id))
                .map((g) => (dead.has(g.parentId) ? { ...g, parentId: resolveLive(g.parentId) } : g));
        }
        const live = new Set(gs.map((g) => g.id));
        const fixed = els.map((e) => (e.groupId && !live.has(e.groupId) ? { ...e, groupId: null } : e));
        if (fixed.some((e, i) => e !== els[i])) { els = fixed; changed = true; }
    }
    return { groups: gs, elements: els };
}

/**
 * Re-order elements so every group — at every nesting level — occupies one
 * contiguous z-order block, while keeping the existing relative order. This is
 * what lets the layers tree render groups as solid blocks.
 */
export function orderElementsByGroups(elements = [], groups = []) {
    const sorted = [...elements].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
    const rank = new Map(sorted.map((e, i) => [e.id, i]));

    const elsByGroup = new Map();
    sorted.forEach((e) => {
        const k = e.groupId || ROOT_KEY;
        if (!elsByGroup.has(k)) elsByGroup.set(k, []);
        elsByGroup.get(k).push(e);
    });
    const groupsByParent = new Map();
    groups.forEach((g) => {
        const k = g.parentId || ROOT_KEY;
        if (!groupsByParent.has(k)) groupsByParent.set(k, []);
        groupsByParent.get(k).push(g);
    });

    const emit = (key, depth) => {
        if (depth > 64) return [];
        const items = [];
        (elsByGroup.get(key) || []).forEach((e) => items.push({ order: rank.get(e.id), els: [e] }));
        (groupsByParent.get(key) || []).forEach((g) => {
            const els = emit(g.id, depth + 1);
            if (els.length) items.push({ order: Math.min(...els.map((e) => rank.get(e.id))), els });
        });
        items.sort((a, b) => a.order - b.order);
        return items.flatMap((i) => i.els);
    };
    return emit(ROOT_KEY, 0);
}

/** Members of a group in z-order (back → front), including nested sub-groups. */
export function getGroupMembers(document, groupId) {
    if (!groupId) return [];
    return getGroupElements(document?.elements || [], document?.groups || [], groupId);
}

/**
 * The two locks a layer can carry, and who each one stops.
 *
 * `locked` is the USER lock — "the person filling this template in must not
 * touch this layer". It is the flag every template already in production
 * stores, and its meaning is unchanged: the admin was never stopped by it and
 * still isn't.
 *
 * `adminLocked` is the ADMIN lock — it pins the layer inside the admin editor
 * so a stray drag cannot nudge finished artwork. It says nothing about what the
 * end user may do, exactly as the user lock says nothing about the admin.
 *
 * The two are independent on purpose: they answer different questions, and a
 * template saved before this split simply has no admin lock on anything.
 *
 * A third, finer setting sits underneath: `lockedSections` closes named parts of
 * the settings panel while leaving the layer editable — see schema/sectionLocks.
 */
export function isLockedFor(el, mode) {
    if (!el) return false;
    return mode === 'user' ? !!el.locked : !!el.adminLocked;
}

const baseElement = (type, overrides = {}) => ({
    id: createId(),
    type,
    name: type.charAt(0).toUpperCase() + type.slice(1),
    x: 80,
    y: 80,
    width: 200,
    height: 60,
    rotation: 0,
    opacity: 1,
    locked: false,
    adminLocked: false,
    lockedSections: [],
    visible: true,
    ...overrides,
});

export function createTextElement(overrides = {}) {
    return baseElement(ELEMENT_TYPES.TEXT, {
        name: 'Text',
        width: 240,
        height: 32, // one line: ceil(fontSize 24 x lineHeight 1.3) — no dead space below

        // How the box follows its text on an edit. 'width' hugs the run (a heading,
        // a label — the box has no width of its own). 'height' keeps the width and
        // grows down, which is what a paragraph frame wants: the column IS the
        // design, and hugging one pulls it out into a single very long line.
        textAutoSize: 'width',
        content: 'Double-click to edit',
        styles: {
            fontFamily: 'Hind Siliguri',
            fontSize: 24,
            fontWeight: 600,
            fontStyle: 'normal',
            textAlign: 'left',
            verticalAlign: 'top',
            color: '#18181b',
            // The glyph fill can be a gradient too (background-clip: text in the
            // editor, a CanvasGradient in the export) — same shape as every other fill.
            textFillType: 'solid',
            textGradientColor1: '#18181b',
            textGradientColor2: '#0d99ff',
            textGradientAngle: 180,
            textGradientCenterX: 50,
            textGradientCenterY: 50,
            lineHeight: 1.3,
            letterSpacing: 0,
            textDecoration: 'none',
            textTransform: 'none',
            whiteSpace: 'pre-wrap',
            backgroundColor: 'transparent',
            backgroundType: 'solid',
            gradientColor1: '#ffffff',
            gradientColor2: '#e4e4e7',
            gradientAngle: 180,
            gradientCenterX: 50,
            gradientCenterY: 50,
            padding: 0,
            paddingTop: 0,
            paddingRight: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            borderWidth: 0,
            borderStyle: 'solid',
            borderColor: '#000000',
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: '#00000040',
            innerShadowBlur: 0,
            innerShadowOffsetX: 0,
            innerShadowOffsetY: 0,
            innerShadowColor: '#00000059',
            mixBlendMode: 'normal',
            ...EFFECT_DEFAULTS,
            textShadowBlur: 0,
            textShadowOffsetX: 0,
            textShadowOffsetY: 0,
            textShadowColor: '#00000073',
            textStrokeWidth: 0,
            textStrokeColor: '#ffffff',
        },
        ...overrides,
    });
}

export function createPlaceholderElement(overrides = {}) {
    return baseElement(ELEMENT_TYPES.PLACEHOLDER, {
        name: 'Placeholder',
        width: 260,
        height: 26, // one line: ceil(fontSize 20 x lineHeight 1.3) — no dead space below

        placeholderKey: 'field_name',
        content: '{{field_name}}',
        editableByUser: true,
        allowedStyles: [
            'fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'color', 'textAlign', 'verticalAlign',
            'lineHeight', 'letterSpacing', 'textDecoration', 'textTransform', 'whiteSpace',
            'backgroundColor', 'backgroundType', 'gradientColor1', 'gradientColor2', 'gradientAngle',
            'gradientCenterX', 'gradientCenterY',
            // The glyph fill's gradient — `color` above is its solid case.
            'textFillType', 'textGradientColor1', 'textGradientColor2', 'textGradientAngle',
            'textGradientCenterX', 'textGradientCenterY',
            'padding', 'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
            'borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomRightRadius', 'borderBottomLeftRadius',
            'shadowBlur', 'shadowOffsetX', 'shadowOffsetY', 'shadowColor',
            'innerShadowBlur', 'innerShadowOffsetX', 'innerShadowOffsetY', 'innerShadowColor',
            'mixBlendMode',
            'textShadowBlur', 'textShadowOffsetX', 'textShadowOffsetY', 'textShadowColor',
            'textStrokeWidth', 'textStrokeColor',
            'hueRotate', 'saturate', 'brightness', 'contrast', 'grayscale', 'sepia', 'invert',
            ...EFFECT_STYLE_KEYS,
        ],
        styles: {
            fontFamily: 'Hind Siliguri',
            fontSize: 20,
            fontWeight: 500,
            fontStyle: 'normal',
            textAlign: 'left',
            verticalAlign: 'top',
            color: '#3f3f46',
            lineHeight: 1.3,
            letterSpacing: 0,
            textDecoration: 'none',
            textTransform: 'none',
            whiteSpace: 'pre-wrap',
            backgroundColor: 'transparent',
            backgroundType: 'solid',
            gradientColor1: '#ffffff',
            gradientColor2: '#e4e4e7',
            gradientAngle: 180,
            gradientCenterX: 50,
            gradientCenterY: 50,
            padding: 0,
            paddingTop: 0,
            paddingRight: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            borderWidth: 0,
            borderStyle: 'solid',
            borderColor: '#000000',
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: '#00000040',
            innerShadowBlur: 0,
            innerShadowOffsetX: 0,
            innerShadowOffsetY: 0,
            innerShadowColor: '#00000059',
            mixBlendMode: 'normal',
            ...EFFECT_DEFAULTS,
            textShadowBlur: 0,
            textShadowOffsetX: 0,
            textShadowOffsetY: 0,
            textShadowColor: '#00000073',
            textStrokeWidth: 0,
            textStrokeColor: '#ffffff',
        },
        ...overrides,
    });
}

export function createImageElement(overrides = {}) {
    return baseElement(ELEMENT_TYPES.IMAGE, {
        name: 'Image',
        width: 180,
        height: 180,
        src: '',
        fit: 'contain',
        // Figma-style crop: the source rectangle to show, in fractions of the
        // picture (see utils/imageCrop). Null = no crop, so `fit` decides.
        crop: null,
        styles: {
            objectPosition: 'center',
            backgroundColor: 'transparent',
            backgroundType: 'solid',
            gradientColor1: '#ffffff',
            gradientColor2: '#e4e4e7',
            gradientAngle: 180,
            gradientCenterX: 50,
            gradientCenterY: 50,
            padding: 0,
            paddingTop: 0,
            paddingRight: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderWidth: 0,
            borderStyle: 'solid',
            borderColor: '#000000',
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: '#00000040',
            innerShadowBlur: 0,
            innerShadowOffsetX: 0,
            innerShadowOffsetY: 0,
            innerShadowColor: '#00000059',
            mixBlendMode: 'normal',
            ...EFFECT_DEFAULTS,
        },
        editableByUser: true,
        ...overrides,
    });
}

export function createShapeElement(shape = DEFAULT_SHAPE, overrides = {}) {
    // Name, box size, corner radius and any parametric fields (polygon sides,
    // star points, arrow head, …) all come from the shape catalogue.
    const def = shapeElementDefaults(shape);
    const r = def.radius;
    return baseElement(ELEMENT_TYPES.SHAPE, {
        name: def.name,
        width: def.width,
        height: def.height,
        shape: def.shape,
        ...def.params,
        styles: {
            fill: '#e4e4e7',
            fillType: 'solid',
            gradientColor1: '#e4e4e7',
            gradientColor2: '#a1a1aa',
            gradientAngle: 180,
            gradientCenterX: 50,
            gradientCenterY: 50,
            stroke: '#a1a1aa',
            strokeWidth: 0,
            strokeStyle: 'solid',
            borderRadius: r,
            borderTopLeftRadius: r,
            borderTopRightRadius: r,
            borderBottomRightRadius: r,
            borderBottomLeftRadius: r,
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: '#00000040',
            innerShadowBlur: 0,
            innerShadowOffsetX: 0,
            innerShadowOffsetY: 0,
            innerShadowColor: '#00000059',
            mixBlendMode: 'normal',
            ...EFFECT_DEFAULTS,
        },
        ...overrides,
    });
}

export function createQrElement(overrides = {}) {
    return baseElement(ELEMENT_TYPES.QRCODE, {
        name: 'QR Code',
        width: 120,
        height: 120,
        content: 'https://sohozkaj.com',
        placeholderKey: null,
        editableByUser: true,
        // The logo in the middle, when there is one. It is `src` on purpose — that
        // is the field the backend lifts into R2 and counts as a media reference
        // (see utils/qrCode for the full reason).
        src: null,
        styles: {
            foreground: '#000000',
            background: '#ffffff',
            margin: 1,
            // Logo plate geometry, in percent of the code's own size. Harmless
            // without a logo, and there from the start so the panel's inputs never
            // start out empty.
            ...QR_LOGO_DEFAULTS,
            // '' = automatic: H once a logo covers modules, M otherwise.
            errorCorrection: '',
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderWidth: 0,
            borderStyle: 'solid',
            borderColor: '#000000',
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: '#00000040',
            innerShadowBlur: 0,
            innerShadowOffsetX: 0,
            innerShadowOffsetY: 0,
            innerShadowColor: '#00000059',
            mixBlendMode: 'normal',
            ...EFFECT_DEFAULTS,
        },
        ...overrides,
    });
}

export function createBarcodeElement(overrides = {}) {
    return baseElement(ELEMENT_TYPES.BARCODE, {
        name: 'Barcode',
        width: 220,
        height: 70,
        content: '123456789012',
        format: 'CODE128',
        placeholderKey: null,
        editableByUser: true,
        styles: {
            lineColor: '#000000',
            background: '#ffffff',
            displayValue: true,
            barWidth: 1.6,
            margin: 4,
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            borderWidth: 0,
            borderStyle: 'solid',
            borderColor: '#000000',
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: '#00000040',
            innerShadowBlur: 0,
            innerShadowOffsetX: 0,
            innerShadowOffsetY: 0,
            innerShadowColor: '#00000059',
            mixBlendMode: 'normal',
            ...EFFECT_DEFAULTS,
        },
        ...overrides,
    });
}

export function createTableElement(overrides = {}) {
    const rows = 3;
    const cols = 3;
    const cells = Array.from({ length: rows }, () =>
        Array.from({ length: cols }, () => '')
    );
    cells[0] = ['Col A', 'Col B', 'Col C'];
    return baseElement(ELEMENT_TYPES.TABLE, {
        name: 'Table',
        width: 360,
        height: 140,
        rows,
        cols,
        cells,
        styles: {
            fontFamily: 'Hind Siliguri',
            fontSize: 14,
            fontWeight: 400,
            color: '#18181b',
            textAlign: 'left',
            verticalAlign: 'middle',
            borderColor: '#d4d4d8',
            borderWidth: 1,
            headerBg: '#f4f4f5',
            cellBg: 'transparent',
            cellPadding: 6,
            borderRadius: 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderBottomLeftRadius: 0,
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: '#00000040',
            innerShadowBlur: 0,
            innerShadowOffsetX: 0,
            innerShadowOffsetY: 0,
            innerShadowColor: '#00000059',
            mixBlendMode: 'normal',
            ...EFFECT_DEFAULTS,
        },
        ...overrides,
    });
}

export function createElementByType(type, overrides = {}) {
    switch (type) {
        case ELEMENT_TYPES.TEXT: return createTextElement(overrides);
        case ELEMENT_TYPES.PLACEHOLDER: return createPlaceholderElement(overrides);
        case ELEMENT_TYPES.IMAGE: return createImageElement(overrides);
        case ELEMENT_TYPES.SHAPE: return createShapeElement(overrides.shape || DEFAULT_SHAPE, overrides);
        case ELEMENT_TYPES.QRCODE: return createQrElement(overrides);
        case ELEMENT_TYPES.BARCODE: return createBarcodeElement(overrides);
        case ELEMENT_TYPES.TABLE: return createTableElement(overrides);
        default: return createTextElement(overrides);
    }
}

/**
 * The element a factory would build for a type — the yardstick both halves of
 * the compact-storage round trip measure against (see schema/compactDocument).
 * Shapes are keyed by kind: a rounded rectangle's default radius is not a
 * triangle's, and neither is its default box.
 */
const KNOWN_ELEMENT_TYPES = new Set(Object.values(ELEMENT_TYPES));
const elementDefaultsCache = new Map();
export function defaultElementFor(type, shape) {
    // An unknown type has no defaults worth guessing at — createElementByType
    // would hand back a text box, and measuring, say, an imported element
    // against that would be meaningless. Both halves of the round trip then
    // leave it exactly as it is.
    if (!type || !KNOWN_ELEMENT_TYPES.has(type)) return null;
    const key = type === ELEMENT_TYPES.SHAPE ? `shape:${shape || DEFAULT_SHAPE}` : String(type);
    if (!elementDefaultsCache.has(key)) {
        elementDefaultsCache.set(key, type === ELEMENT_TYPES.SHAPE
            ? createElementByType(type, { shape: shape || DEFAULT_SHAPE })
            : createElementByType(type));
    }
    return elementDefaultsCache.get(key);
}

/**
 * Put back everything a compacted element left out. Values the document does
 * carry always win, so this can never overwrite a real edit — including one that
 * happens to be the old default after the default itself changed.
 */
export function hydrateElementDefaults(el) {
    if (!el || typeof el !== 'object') return el;
    const ref = defaultElementFor(el.type, el.shape);
    if (!ref) return el;
    return {
        ...ref,
        ...el,
        styles: { ...(ref.styles || {}), ...(el.styles || {}) },
    };
}

export function normalizeDocument(doc) {
    if (!doc || typeof doc !== 'object') return createEmptyDocument();

    // Frames are the model; a template saved before they existed carries a single
    // `canvas`, which becomes frame 1 at the scene origin. Element coordinates were
    // already relative to that origin, so nothing has to be moved.
    const rawFrames = Array.isArray(doc.frames) ? doc.frames.filter(Boolean) : [];
    const frames = rawFrames.length
        ? rawFrames.map((f, i) => normalizeFrame(f, i))
        : [normalizeFrame({ ...(doc.canvas || {}), x: 0, y: 0, name: 'Frame 1' }, 0)];
    const frameIds = new Set(frames.map((f) => f.id));

    const elements = Array.isArray(doc.elements) ? doc.elements.map((el, i) => ({
        // Documents are STORED compacted — every value still equal to its type's
        // factory default is left out (see compactDocument). Putting the defaults
        // back here is what lets the rest of the editor keep assuming a fully
        // populated element, and it is a no-op for older, fully-written templates.
        ...hydrateElementDefaults(el),
        id: el.id || createId(),
        visible: el.visible !== false,
        locked: !!el.locked,
        adminLocked: !!el.adminLocked,
        lockedSections: Array.isArray(el.lockedSections) ? el.lockedSections.filter(Boolean) : [],
        opacity: el.opacity ?? 1,
        rotation: el.rotation || 0,
        zIndex: el.zIndex ?? i,
        groupId: el.groupId || null,
        // An unknown (or missing) frame is resolved by geometry, which is both the
        // migration path for old templates and the repair path for a frame that was
        // deleted while its elements stayed behind.
        frameId: el.frameId && frameIds.has(el.frameId)
            ? el.frameId
            : frameForBox(frames, el),
        // Per-character colours are offsets into the text; a template whose text was
        // edited elsewhere (an API import, a hand-edited JSON) can carry spans that
        // now run past the end of it, so they are clamped on the way in.
        ...(Array.isArray(el.colorSpans) && el.colorSpans.length
            ? { colorSpans: normalizeColorSpans(el.colorSpans, String(el.content ?? '').length) }
            : null),
    })) : [];

    // Keep group members contiguous in z-order — at EVERY nesting level — so the
    // layers tree renders each group (and sub-group) as a solid block.
    const rawGroups = Array.isArray(doc.groups)
        ? doc.groups.filter((g) => g && g.id).map((g) => ({
            id: String(g.id),
            name: g.name || 'Group',
            parentId: g.parentId ? String(g.parentId) : null,
        }))
        : [];
    // Prune first so ordering never sees dangling/among-dead parents, then order.
    const cleaned = pruneGroups(rawGroups, elements);
    const contiguous = orderElementsByGroups(cleaned.elements, cleaned.groups)
        .map((el, i) => ({ ...el, zIndex: i }));
    const pruned = { groups: cleaned.groups, elements: contiguous };

    return withFrames(
        { version: TEMPLATE_VERSION, elements: pruned.elements, groups: pruned.groups },
        frames,
    );
}

export function getSortedElements(elements = []) {
    return [...elements].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
}
